import React, { useEffect, useMemo, useState } from "react";
import { useAuthoredPostsSearch } from "@/main/react/Dialog/useAuthoredPostsSearch";
import { Localizer } from "@/main/react/Types";
import { ClientFactory } from "@/main/common/ClientFactory";
import { Check, ListPlus, Plus, Search, SquarePen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SafeImage from "@/main/util/SafeImage";
import { Spinner } from "@/components/ui/spinner";
import {
    emitDiscussionAddedToSeries,
    emitSeriesAddedToDiscussion,
} from "@/main/common/EventRegistry";
import { toast } from "sonner";
import { AjaxSeriesRestControllerApi, DiscussionsToAddToSeriesDTO, SeriesDTO } from "@/generated-client";
import DateFormatter from "@/main/common/DateFormatter";
import SimpleDialog, { openSimpleDialog } from "@/main/react/Dialog/SimpleDialog";
import logger from "@/lib/logger";
import { PaginatedListEmptyState } from "@/main/react/list/PaginatedListState";
import { showCreateSeriesDialog } from "@/main/react/Dialog/UpdateSeriesSettingsDialog";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import { getHideSensitiveMediaPreference, getSensitivePreferenceMode } from "@/main/common/SensitivePreference";
import {SMALL_THUMBNAIL_IMAGE_SIZE} from "@/main/util/ImageUrlHelper";
import { navigateToUrl } from "@/main/common/BrowserNavigation";
import { buildNewPostInSeriesUrl } from "@/main/react/editor/discussion/seriesPrefill";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { getDisplayUsername } from "@/main/common/UsernameDisplayUtils";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import {type WithDiscussionAuthorAvatar} from "@/main/common/DiscussionAuthorAvatar";

type Props = {
    seriesPublicId: string;
    seriesSummary?: SeriesDTO;
    localizer: Localizer;
    onClose?: () => void;
};

type TabKey = "authored" | "participated" | "series";

const TAB_KEYS: TabKey[] = ["authored", "participated", "series"];

/**
 * The bootstrap returns only the most recent candidates per tab, so the search box has to reach the
 * server for anything older. Debounced so a typed word costs one round of requests, not one per key.
 */

/**
 * One candidate in the add-post picker. Round 38 D6 (`SERIES-20`): a card never writes. It is one of
 * three states, each saying in words what it is: `add` (tap to pick), `added` (picked, tap to unpick)
 * and `in_series` (already in the series: a status, not a control). The picker commits the picks from
 * its footer.
 */
export type AddToSeriesCardState = "add" | "added" | "in_series";

export function DiscussionAddToSeriesCardDisplayer(props: {
    discussion: DiscussionsToAddToSeriesDTO;
    localizer: Localizer;
    /** Absent: derived from the server's `isDiscussionAdded` (in the series or not). */
    state?: AddToSeriesCardState;
    isToggling?: boolean;
    onToggleAddedToSeries?: () => Promise<void> | void;
}) {
    const { discussion, localizer, onToggleAddedToSeries, isToggling } = props;
    const state: AddToSeriesCardState = props.state ?? (discussion.isDiscussionAdded ? "in_series" : "add");
    const discussionWithAuthorAvatar = discussion as WithDiscussionAuthorAvatar<DiscussionsToAddToSeriesDTO>;
    const discussionWithFlags = discussion as DiscussionsToAddToSeriesDTO & { sensitive?: boolean | null };
    const df = useMemo(() => new DateFormatter(localizer), [localizer]);
    const isSensitive = Boolean(discussionWithFlags.sensitive);
    const sensitivePreference = getSensitivePreferenceMode();
    const hideSensitiveMedia = getHideSensitiveMediaPreference();

    const formattedCreated = discussion.createdAt ? df.timeSince(discussion.createdAt) : "";
    const added = state === "added";
    const inSeries = state === "in_series";
    // `border-border`, not `border-neutral-200`: the latter is a raw Tailwind palette step with no dark
    // variant, so it painted a light-grey hairline on a dark card surface.
    const borderClass = added ? "border border-primary" : "border border-border";

    const handleClick = async (e?: React.SyntheticEvent) => {
        try {
            e?.stopPropagation();
            e?.preventDefault();
        } catch {
            /* ignore */
        }

        if (!onToggleAddedToSeries || isToggling || inSeries) return;
        await onToggleAddedToSeries();
    };

    const onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            await handleClick(e);
        }
    };

    const warningCopy = getMaskedContentWarningCopy(localizer, { isSensitive });

    return (
        <div
            className={`relative w-full min-w-0 max-w-full rounded-xl p-2 outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 ${borderClass} ${inSeries ? "cursor-default" : "cursor-pointer"}`}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={onKeyDown}
            aria-pressed={inSeries ? undefined : added}
            aria-disabled={inSeries ? true : undefined}
            aria-label={inSeries ? undefined : localizer("series.add_posts.item_label", discussion.title ?? "")}
            data-add-state={state}
            data-playwright-id="series-add-discussion-card"
            data-discussion-public-id={discussion.publicId ?? ""}
        >
            <div className="flex min-w-0 gap-2 items-start">
                {discussion.imageUrl && discussion.imageUrl.length > 0 ? (
                    <div className="hidden shrink-0 sm:block object-cover rounded-md bg-[var(--background2)] h-24 w-24 sm:w-24 sm:h-24">
                        <SensitiveMediaGuard
                            isSensitive={isSensitive}
                            hideSensitiveMedia={hideSensitiveMedia}
                            sensitivePreference={sensitivePreference}
                            variant="card"
                            overlayStacking="below-link-overlay"
                            badgeLabel={warningCopy.badgeLabel}
                            label={warningCopy.label}
                            helperText={warningCopy.helperText}
                            revealText={warningCopy.revealText}
                            className="h-full rounded-md"
                        >
                            <SafeImage
                                src={discussion.imageUrl}
                                alt={discussion.title ?? ""}
                                seavienImageSize={SMALL_THUMBNAIL_IMAGE_SIZE}
                                wrapperClassName="w-full h-full"
                                className="object-cover w-full h-full rounded-md"
                                onError={() => {}}
                            />
                        </SensitiveMediaGuard>
                    </div>
                ) : null}

                <div className="flex-1 min-w-0">
                    {/* The corner slot is IN FLOW and floated, not `absolute`.
                        It holds either a 20px bookmark glyph or a status chip that is a full localized
                        sentence — "Waiting for group leaders" is ~159px in English and longer in Spanish.
                        While it was `absolute top-2 right-2` nothing reserved its width, and the fixed
                        `pe-24` gutter that stood in for that reservation was a GUESS about a localized
                        string: it reserved 96px against a chip that needs up to ~159px, so the chip
                        painted over the title in every locale, and it spent those 96px of a 229px title
                        on the majority of cards that show only the glyph. Floated, the title's first
                        line wraps around whatever width the chip actually took and later lines recover
                        the full column — nothing is estimated. `relative z-20` is kept: the slot still
                        has to sit above the sensitive-media overlay in the thumbnail column.
                        Pinned across all four pickers by `dialogCardTitleGutter.spec.tsx`. */}
                    <div className="relative z-20 float-right ms-2" data-playwright-id="picker-card-corner-slot">
                        {isToggling ? (
                            <Spinner className="w-5 h-5 text-neutral" />
                        ) : (
                            // Words, not the borrowed Save glyph (`SERIES-20`): what a tap does, or that
                            // there is nothing to do.
                            <span
                                className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${added
                                    ? "border-primary bg-primary text-primary-foreground"
                                    : inSeries
                                        ? "border-border bg-muted text-muted-foreground"
                                        : "border-border bg-card text-text-main"}`}
                                data-playwright-id="series-add-discussion-card-state"
                            >
                                {added ? <Check className="size-3.5" aria-hidden="true" /> : inSeries ? null : <Plus className="size-3.5" aria-hidden="true" />}
                                {added
                                    ? localizer("series.add_posts.added")
                                    : inSeries
                                        ? localizer("series.add_posts.in_series")
                                        : localizer("series.add_posts.add")}
                            </span>
                        )}
                    </div>
                    <h4 className="wrap-anywhere font-semibold text-base">
                        <span className="inline-flex flex-wrap items-center gap-2">
                            <span>{discussion.title}</span>
                            {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                        </span>
                    </h4>

                    <AuthorAttribution localizer={localizer}
                        name={getDisplayUsername(discussion.authorUsername, localizer)}
                        imageUrl={discussionWithAuthorAvatar.authorProfileImageUrl}
                        avatarSeed={discussion.authorUsername}
                        details={formattedCreated ? <time dateTime={discussion.createdAt}>{formattedCreated}</time> : null}
                        className="mt-1 text-xs"
                        avatarClassName="size-5"
                    />

                    <p className="mt-2 text-sm line-clamp-3 wrap-anywhere">
                        {discussion.excerpt}
                    </p>
                </div>
            </div>
        </div>
    );
}

export function AddDiscussionsToSeriesDialogInner({ seriesPublicId, seriesSummary, localizer, onClose }: Props) {
    const [loading, setLoading] = useState(true);
    const [authoredDiscussions, setAuthoredDiscussions] = useState<DiscussionsToAddToSeriesDTO[] | null>(null);
    const [participatedDiscussions, setParticipatedDiscussions] = useState<DiscussionsToAddToSeriesDTO[] | null>(null);
    const [seriesDiscussions, setSeriesDiscussions] = useState<DiscussionsToAddToSeriesDTO[] | null>(null);
    const [tab, setTab] = useState<TabKey>("authored");
    const [query, setQuery] = useState("");
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    // Round 38 D6: picks, in the order they were made. Nothing is written until `commitStaged`.
    const [stagedIds, setStagedIds] = useState<string[]>([]);
    const [committing, setCommitting] = useState(false);
    // Per-tab "there are older ones behind this page", straight from the bootstrap.
    const [truncated, setTruncated] = useState<Record<TabKey, boolean>>({
        authored: false,
        participated: false,
        series: false,
    });

    const seriesClient: AjaxSeriesRestControllerApi = useMemo(
        () => ClientFactory.getAjaxSeriesRestControllerApi(),
        []
    );

    const goToCreateDiscussion = React.useCallback(() => {
        onClose?.();
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                // FI-593: a NEW draft with this series pre-selected, not the bare reused composer.
                navigateToUrl(buildNewPostInSeriesUrl(seriesPublicId), { source: "series-dialog-create-discussion" });
            }, 0);
        }
    }, [onClose, seriesPublicId]);

    const goToExploreDiscussions = React.useCallback(() => {
        onClose?.();
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                navigateToUrl("/", { source: "series-dialog-explore-discussions" });
            }, 0);
        }
    }, [onClose]);

    const openCreateSeries = React.useCallback(() => {
        onClose?.();
        if (typeof window !== "undefined") {
            window.setTimeout(() => {
                showCreateSeriesDialog(localizer);
            }, 0);
        }
    }, [localizer, onClose]);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setTab("authored");
        setQuery("");

        ClientFactory.getSeriesDiscussionsModalBootstrap(seriesPublicId)
            .then(({ data }) => {
                if (!mounted) return;

                const authored = data?.authoredDiscussions ?? [];
                const participated = data?.participatedDiscussions ?? [];
                const seriesItems = data?.seriesDiscussions ?? [];

                setAuthoredDiscussions(authored);
                setParticipatedDiscussions(participated);
                setSeriesDiscussions(seriesItems);
                setTruncated({
                    authored: Boolean(data?.authoredTruncated),
                    participated: Boolean(data?.participatedTruncated),
                    series: Boolean(data?.seriesTruncated),
                });

                if (authored.length > 0) {
                    setTab("authored");
                } else if (participated.length > 0) {
                    setTab("participated");
                } else if (seriesItems.length > 0) {
                    setTab("series");
                }
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                logger.error("Failed loading discussions JSON for series modal", err);
                toast.error(localizer("series.modal.toast_error"));
            })
            .finally(() => {
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [seriesPublicId, localizer]);

    const startToggling = (id: string) => setTogglingIds((prev) => new Set(prev).add(id));
    const stopToggling = (id: string) =>
        setTogglingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

    const updateDiscussionLists = (discussionPublicId: string, nextAddedValue: boolean) => {
        const update = (prev: DiscussionsToAddToSeriesDTO[] | null) => {
            if (!prev) return prev;
            return prev.map((item) =>
                item.publicId === discussionPublicId
                    ? { ...item, isDiscussionAdded: nextAddedValue }
                    : item
            );
        };

        setAuthoredDiscussions(update);
        setParticipatedDiscussions(update);
        setSeriesDiscussions(update);
        // A card the member reached through search lives only in the search results, so patching the
        // bootstrap lists alone would leave its bookmark icon stale until the next keystroke.
        setSearchResults((prev) => prev && {
            authored: update(prev.authored) ?? prev.authored,
            participated: update(prev.participated) ?? prev.participated,
            series: update(prev.series) ?? prev.series,
        });
    };

    const toggleStaged = (discussion: DiscussionsToAddToSeriesDTO) => {
        const publicId = discussion.publicId;
        if (loading || committing || !publicId || discussion.isDiscussionAdded) return;
        setStagedIds((prev) => (prev.includes(publicId) ? prev.filter((id) => id !== publicId) : [...prev, publicId]));
    };

    /**
     * Round 38 D6: the one write. Adds every staged post, in pick order, through the same per-post
     * endpoint and the same events the instant toggle used, so the series page updates exactly as before.
     * Stops at the first failure and keeps what is still staged, so a retry sends only the rest.
     */
    async function commitStaged(candidatesById: Map<string, DiscussionsToAddToSeriesDTO>) {
        if (committing || stagedIds.length === 0) return;
        setCommitting(true);
        const remaining = [...stagedIds];
        try {
            while (remaining.length > 0) {
                const publicId = remaining[0];
                const discussion = candidatesById.get(publicId) ?? ({ publicId } as DiscussionsToAddToSeriesDTO);
                startToggling(publicId);
                try {
                    const response = await seriesClient.addDiscussionToSeries(seriesPublicId, publicId);
                    const refreshedSeries = response.data;
                    updateDiscussionLists(publicId, true);
                    const updatedDiscussion = { ...discussion, isDiscussionAdded: true };
                    // Series membership is a field of the discussion page-data response, and
                    // ClientFactory.invalidateDiscussionPageData's javadoc says every mutation that changes
                    // such a field MUST call it (2026-08-04).
                    ClientFactory.invalidateDiscussionPageData(publicId);
                    emitDiscussionAddedToSeries({
                        seriesPublicId,
                        discussion: updatedDiscussion,
                        series: refreshedSeries,
                    });
                    emitSeriesAddedToDiscussion({
                        discussionPublicId: publicId,
                        series: refreshedSeries ?? seriesSummary ?? { publicId: seriesPublicId },
                    });
                    remaining.shift();
                    setStagedIds([...remaining]);
                } finally {
                    stopToggling(publicId);
                }
            }
            // The list behind the dialog shows the posts; one sentence covers the one-post case.
            if (stagedIds.length === 1) {
                toast.success(localizer("series.modal.toast_added"));
            }
            onClose?.();
        } catch (err) {
            logger.error("Failed adding staged discussions to series", err);
            toast.error(localizer("series.modal.toast_error"));
        } finally {
            setCommitting(false);
        }
    }

    // Searching hits the per-source endpoint, never modal-bootstrap: the chooser specs assert the
    // bootstrap is read exactly once per open. All three sources are queried together so the
    // "jump to the tab that has matches" behaviour below keeps working — it needs counts it does not
    // otherwise have, and the responses are one capped page each. The debounce/supersede/null-while-
    // in-flight state is the shared `useAuthoredPostsSearch` (FI-594), which the composer's link panel
    // and Embed dialog drive with the series-free own-posts read.
    const searchAllSources = React.useCallback(
        (trimmed: string) => Promise.all(
            TAB_KEYS.map((source) =>
                ClientFactory.searchSeriesDiscussionCandidates(seriesPublicId, source, trimmed)
                    .then(({ data }) => data ?? []),
            ),
        ).then(([authored, participated, seriesItems]) => ({ authored, participated, series: seriesItems })),
        [seriesPublicId],
    );
    const { trimmedQuery, results: searchResults, setResults: setSearchResults } =
        useAuthoredPostsSearch<Record<TabKey, DiscussionsToAddToSeriesDTO[]>>(query, searchAllSources, (err) => {
            // eslint-disable-next-line no-console
            logger.error("Failed searching discussions for series modal", err);
            toast.error(localizer("series.modal.toast_error"));
            // Land on a definite empty result rather than an endless spinner.
            return { authored: [], participated: [], series: [] };
        });

    const bootstrapLists = useMemo<Record<TabKey, DiscussionsToAddToSeriesDTO[] | null>>(() => ({
        authored: authoredDiscussions,
        participated: participatedDiscussions,
        series: seriesDiscussions,
    }), [authoredDiscussions, participatedDiscussions, seriesDiscussions]);

    // Null means "nothing to show yet" — either the bootstrap or the search is still in flight — and
    // renders the spinner. An empty array is a real answer.
    const visibleFor = React.useCallback((source: TabKey): DiscussionsToAddToSeriesDTO[] | null => {
        if (!trimmedQuery) return bootstrapLists[source];
        return searchResults ? searchResults[source] : null;
    }, [bootstrapLists, searchResults, trimmedQuery]);

    React.useEffect(() => {
        if (!trimmedQuery || !searchResults) return;
        if (searchResults[tab].length > 0) return;

        const nextTab = TAB_KEYS.find((candidate) => searchResults[candidate].length > 0);
        if (nextTab && nextTab !== tab) {
            setTab(nextTab);
        }
    }, [trimmedQuery, tab, searchResults]);

    /** Every candidate currently known (bootstrap + search), so a pick made through search commits too. */
    function collectCandidatesById(): Map<string, DiscussionsToAddToSeriesDTO> {
        const byId = new Map<string, DiscussionsToAddToSeriesDTO>();
        const lists = [
            authoredDiscussions, participatedDiscussions, seriesDiscussions,
            searchResults?.authored ?? null, searchResults?.participated ?? null, searchResults?.series ?? null,
        ];
        lists.forEach((list) => list?.forEach((item) => {
            if (item.publicId && !byId.has(item.publicId)) {
                byId.set(item.publicId, item);
            }
        }));
        return byId;
    }

    function renderDiscussionEntry(discussion: DiscussionsToAddToSeriesDTO) {
        return (
            <DiscussionAddToSeriesCardDisplayer
                key={discussion.publicId}
                discussion={discussion}
                localizer={localizer}
                state={discussion.isDiscussionAdded
                    ? "in_series"
                    : (discussion.publicId && stagedIds.includes(discussion.publicId) ? "added" : "add")}
                isToggling={Boolean(discussion.publicId && togglingIds.has(discussion.publicId))}
                onToggleAddedToSeries={() => toggleStaged(discussion)}
            />
        );
    }

    function renderTabContent(source: TabKey) {
        const visibleItems = visibleFor(source);

        // `visibleItems === null` covers both the opening bootstrap and an in-flight search.
        if (loading || visibleItems === null) {
            return (
                <div className="w-full flex items-center justify-center py-6">
                    <Spinner className="w-10 h-10 text-neutral" />
                </div>
            );
        }

        if (visibleItems.length > 0) {
            return (
                <div className="flex w-full flex-col gap-2 max-h-[70vh] overflow-y-auto no-scrollbar pr-2">
                    {visibleItems.map((discussion) => renderDiscussionEntry(discussion))}
                </div>
            );
        }

        if (trimmedQuery) {
            return (
                <PaginatedListEmptyState
                    icon={Search}
                    title={localizer("series.modal.discussion_search_empty.title")}
                    description={localizer("series.modal.discussion_search_empty.description")}
                />
            );
        }

        const action = source === "authored"
            ? {
                label: localizer("series.modal.new_discussion"),
                onClick: goToCreateDiscussion,
                icon: SquarePen,
            }
            : source === "participated"
                ? {
                    label: localizer("literal.explore_discussions"),
                    onClick: goToExploreDiscussions,
                    icon: Search,
                }
                : {
                    label: localizer("series.modal.new_series"),
                    onClick: openCreateSeries,
                    icon: ListPlus,
                };

        const emptyTitle = source === "authored"
            ? localizer("series.modal.empty.authored.title")
            : source === "participated"
                ? localizer("series.modal.empty.participated.title")
                : localizer("series.modal.empty.series.title");
        const emptyDescription = source === "authored"
            ? localizer("series.modal.empty.authored.description")
            : source === "participated"
                ? localizer("series.modal.empty.participated.description")
                : localizer("series.modal.empty.series.description");

        return (
            <PaginatedListEmptyState
                icon={action.icon}
                title={emptyTitle}
                description={emptyDescription}
                action={action}
            />
        );
    }

    return (
        <div className="relative w-full" data-playwright-id="series-add-discussion-dialog">
            <div className="flex flex-col gap-4 items-start justify-start w-full">
                <div className="relative w-full">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral" aria-hidden={true} />
                    <input
                        type="search"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={localizer("series.modal.discussion_search_placeholder")}
                        aria-label={localizer("series.modal.discussion_search_placeholder")}
                        className="w-full rounded-lg border border-border bg-input/70 py-3 pl-10 pr-4 text-sm [@media(pointer:coarse)]:text-base text-text-main outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
                        data-playwright-id="series-add-discussion-search"
                    />
                </div>

                {!trimmedQuery && truncated[tab] ? (
                    <p
                        // `text-muted-foreground`: this hint is body copy, and the sibling hint in
                        // `GroupAddDiscussionToGroupDialog` already reads that way.
                        className="w-full text-xs text-muted-foreground"
                        data-playwright-id="series-add-discussion-truncated-hint"
                    >
                        {localizer("shared.chooser.candidates_truncated")}
                    </p>
                ) : null}

                <Tabs className="w-full" value={tab} onValueChange={(value) => setTab(value as TabKey)}>
                    <TabsList variant={"line"} className="w-full justify-center" data-playwright-id="series-add-discussion-tabs">
                        <TabsTrigger value="authored" data-playwright-id="series-add-discussion-tab-authored">
                            {localizer("series.modal.tab_authored")}
                        </TabsTrigger>
                        <TabsTrigger value="participated" data-playwright-id="series-add-discussion-tab-participated">
                            {localizer("series.modal.tab_participated")}
                        </TabsTrigger>
                        <TabsTrigger value="series" data-playwright-id="series-add-discussion-tab-series">
                            {localizer("series.modal.tab_series")}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="authored" className={"w-full max-w-full"} data-playwright-id="series-add-discussion-content-authored">
                        {renderTabContent("authored")}
                    </TabsContent>

                    <TabsContent value="participated" className={"w-full max-w-full"} data-playwright-id="series-add-discussion-content-participated">
                        {renderTabContent("participated")}
                    </TabsContent>

                    <TabsContent value="series" className={"w-full max-w-full"} data-playwright-id="series-add-discussion-content-series">
                        {renderTabContent("series")}
                    </TabsContent>
                </Tabs>

                {/* Round 38 D6: the picker's one primary, present only once something is picked. */}
                {stagedIds.length > 0 ? (
                    <div className="sticky bottom-0 flex w-full justify-end border-t border-border bg-card pt-3">
                        <Button
                            type="button"
                            onClick={() => void commitStaged(collectCandidatesById())}
                            disabled={committing}
                            data-testid="series-add-posts-submit"
                            data-playwright-id="series-add-posts-submit"
                        >
                            {committing ? <Spinner className="size-4" /> : <ListPlus aria-hidden={true} />}
                            {localizer("series.add_posts.submit", stagedIds.length)}
                        </Button>
                    </div>
                ) : null}
            </div>
        </div>
    );
}

/**
 * Round 38 D20 (`SERIES-35`): the picker names the task and the series ("Add posts to {series}"). A caller
 * that has no title to hand keeps the old noun rather than an empty quote.
 */
export function getAddPostsDialogTitle(localizer: Localizer, seriesSummary?: SeriesDTO): string {
    const title = seriesSummary?.title?.trim();
    return title ? localizer("series.add_posts.title", title) : localizer("series.modal.discussions_title");
}

export function showAddDiscussionsToSeriesDialog(seriesPublicId: string, localizer: Localizer, seriesSummary?: SeriesDTO) {
    openSimpleDialog(({ open, onOpenChange }) => (
        <SimpleDialog
            open={open}
            onOpenChange={onOpenChange}
            title={
                <div id="discussions-modal-title">
                    {getAddPostsDialogTitle(localizer, seriesSummary)}
                </div>
            }
            width="xl"
        >
            <AddDiscussionsToSeriesDialogInner
                seriesPublicId={seriesPublicId}
                seriesSummary={seriesSummary}
                localizer={localizer}
                onClose={() => onOpenChange(false)}
            />
        </SimpleDialog>
    ));
}
